import React, { Suspense, useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import GlobalStyle from "./globalStyles";
import { urlPaths, config } from "./utils/config";
import { fetchAPI } from "./utils/fetch";
import { isPaidPlanActive } from "./utils/plans";
import { lazyPage } from "./utils/lazyPage";
import { useTheme } from "./hooks/ThemeConfig";
import { getHostNameConfig } from "./utils/getHostNameConfig";
import StickyCTA from "./components/Sticky/stickyComponent";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/toaster";
import SalesPages from "./layout/SalesPages";
import RouteFallback from "./components/shared/RouteFallback";
import Seo from "./components/shared/Seo";

// Every page is its own chunk so a visitor only downloads the page they open,
// not the whole app. SalesPages stays eager because it wraps the homepage.
const LoginPage = lazyPage(() => import("./pages/Login"));
const RegisterPage = lazyPage(() => import("./pages/RegisterOne"));
const MainPage = lazyPage(() => import("./pages/platform/Main"));
const RequestNewPasswordPage = lazyPage(() => import("./pages/RequestNewPassword"));
const NewPasswordPage = lazyPage(() => import("./pages/NewPassword"));
const SettingsPage = lazyPage(() => import("./pages/platform/Settings"));

const Unauthorized = lazyPage(() => import("./pages/Unauthorized"));
const AboutUs = lazyPage(() => import("./pages/sales/AboutUs"));
const Results = lazyPage(() => import("./pages/sales/Results"));
const Feature = lazyPage(() => import("./pages/sales/Feature"));

const Pricing = lazyPage(() => import("./pages/sales/Pricing"));
const PrivacyPolicyPage = lazyPage(() => import("./pages/sales/PrivacyPolicy"));
const TermsPage = lazyPage(() => import("./pages/sales/TermsOfService"));
const CheckoutRedirectPage = lazyPage(() => import("./pages/CheckoutRedirect"));
const ContactPage = lazyPage(() => import("./pages/platform/Contact"));
const ExtensionLoginPage = lazyPage(() => import("./pages/ExtensionLoginPage"));
const HomeV1 = lazyPage(() => import("./pages/sales/HomeV1"));
const Commenterhome = lazyPage(() => import("./pages/sales/Commenterhome"));
const PublicPages = lazyPage(() => import("./layout/PublicPages"));
const MainPages = lazyPage(() => import("./layout/MainPage"));
const NotFoundPage = lazyPage(() => import("./pages/NotFound"));
const PostedComments = lazyPage(() => import("./pages/platform/PostedComments"));
const Posts = lazyPage(() => import("./pages/platform/Posts"));
const PostList = lazyPage(() => import("./pages/platform/Posts/list/PostList"));
const PeopleList = lazyPage(() => import("./pages/platform/Posts/people/PeopleList"));
const PeopleSingle = lazyPage(() => import("./pages/platform/Posts/people/PeopleSingle"));
const QRLoginPage = lazyPage(() => import("./pages/platform/Qr-login"));

// Tracking Dashboard
const FocusPage = lazyPage(() => import("./pages/platform/FocusPage"));
const InboxWrapper = lazyPage(() => import("./pages/platform/InboxWrapper"));
const AnalyticsPage = lazyPage(() => import("./pages/platform/AnalyticsPage"));
const OnboardingPage = lazyPage(() => import("./pages/onboarding/OnboardingPage"));
const WhatFocusData = lazyPage(() => import("./pages/onboarding/WhatFocusData"));
const SettingsPageNew = lazyPage(() => import("./pages/settings/SettingsPage"));
const PreferencesPage = lazyPage(() => import("./pages/settings/PreferencesPage"));

// Retired pages (Oct 2026): no live screen links to them and they had no visits,
// so their routes below are commented out and these URLs fall through to the
// NotFound page. Delete them and their files once nobody has missed them.
// const AchievementsPage = lazyPage(() => import("./pages/platform/Achievements"));
// const RequestAFeaturePage = lazyPage(() => import("./pages/platform/RequestAFeature"));
// const DashboardComingSoon = lazyPage(() => import("./pages/platform/crm/DashboardComingSoon"));
// const SessionExpired = lazyPage(() => import("./pages/SessionExpired"));
// const SuccessSubscriptionPage = lazyPage(() => import("./pages/platform/SuccessSubscription"));
// const Homepage = lazyPage(() => import("./pages/sales/Homepage"));
// const ComingSoon2 = lazyPage(() => import("./pages/sales/ComingSoon2"));
// const ComingSoon = lazyPage(() => import("./pages/sales/ComingSoon"));
// const Time = lazyPage(() => import("./pages/Time"));
// const SuccessRegisterPage = lazyPage(() => import("./pages/SuccessRegister"));
// const EngagementList = lazyPage(() => import("./layout/EngagementList"));
// const Marketplace = lazyPage(() => import("./layout/Marketplace"));
// const MyCommentList = lazyPage(() => import("./layout/MyCommentList"));
// const ProPlan = lazyPage(() => import("./layout/ProPlan"));
// const YearlyPlan = lazyPage(() => import("./layout/YearlyPlan"));
// const YearlyPlanEmail = lazyPage(() => import("./layout/YearlyPlanEmail"));
// const BlackFriday = lazyPage(() => import("./layout/BlackFriday"));
// const Professional = lazyPage(() => import("./layout/Professional"));
// const Tutorial = lazyPage(() => import("./pages/platform/Tutorial"));
// const CustomNew = lazyPage(() => import("./pages/platform/CustomNew"));
// const Dashboard = lazyPage(() => import("./pages/platform/Dashboard"));
// const SurveySuccess = lazyPage(() => import("./pages/SurveySuccess"));
// const MessageCenterNew = lazyPage(() => import("./pages/platform/crm/MessageCenterNew"));

function App() {
  const scriptRef = useRef(null);
  const firstLoadChatsRef = useRef(true); // track first load
  const firstLoadMessagesRef = useRef(true); // track first load
  const queryClient = new QueryClient();

  const { featureToggle, hostnameConfig } = useTheme();

  useEffect(() => {
    let appended = false;
    const timer = setTimeout(() => {
      scriptRef.current = document.createElement("script");
      scriptRef.current.src =
        "//code.tidio.co/zidoazq0yjqcnntterntgfgl8fgcwnxp.js";
      document.body.appendChild(scriptRef.current);
      appended = true;
    }, 5000);

    return () => {
      clearTimeout(timer);
      if (appended && scriptRef.current) {
        scriptRef.current.remove();
      }
    };
  }, []);

  const FallbackRedirect = () => {
    const userDataStr = localStorage.getItem("userData");
    // Logged-out visitors get a real 404; sending them into the workspace would
    // bounce them to /login instead.
    if (!userDataStr) {
      return <NotFoundPage />;
    }
    try {
      const userData = JSON.parse(userDataStr);
      if (userData.requiresMigration) {
        // If preference is already set, respect it, otherwise send to migration flow
        if (userData.workspacePreference) {
          return <Navigate to={userData.workspacePreference === 'focus' ? "/focus" : "/commenting"} replace />;
        }
        return <Navigate to="/commenting?migration=true" replace />;
      }
      if (userData.workspacePreference === 'focus') {
        return <Navigate to="/focus" replace />;
      }
    } catch (e) { }
    // Commenting is the default workspace; only an explicit "focus" preference
    // overrides it.
    return <Navigate to="/commenting" replace />;
  };

  // useEffect(() => {
  //   const userData = localStorage.getItem("userData");
  //   if (userData) {
  //     const user = JSON.parse(userData);
  //     // If user is logged in and has a plan/trial, trigger pre-fetch
  //     if (user?.sevenDayTrial || user?.paidStatus) {
  //       chatCacheService.preFetch(30);
  //     }
  //   }
  // }, []);

  const ProtectedUnipileRoute = ({ children, requireAuth = true }) => {
    const [userDetail, setUserDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);

      function getCookie(name) {
        const match = document.cookie.split("; ").find(row => row.startsWith(name + "="));
        if (!match) return null;
        // Value may legitimately contain "=", so only split on the first one.
        const raw = match.slice(match.indexOf("=") + 1);
        const value = decodeURIComponent(raw);
        // Guard against sentinel strings that should never be treated as a real
        // value (e.g. a cookie stamped with a stringified undefined/null).
        if (!value || value === "undefined" || value === "null") return null;
        return value;
      }

      const bootstrap = async () => {
        try {
          /**STEP 1 — Resolve email*/
          const email = getCookie("handoff_email");

          /**STEP 2 — Hydrate userData*/
          let currentUserData = localStorage.getItem("userData");
          if (currentUserData) {
            currentUserData = JSON.parse(currentUserData);
          }

          if (email) {
            const userResponse = await fetchAPI.get(
              `${config.urlPlatform}/api/v2/auth/user?email=${encodeURIComponent(email)}`,
              { withCredentials: true }
            );
            const fetchedUserData = userResponse?.data?.data || userResponse?.data;
            if (fetchedUserData?.email) {
              localStorage.setItem("userData", JSON.stringify(fetchedUserData));
              currentUserData = fetchedUserData;

              const expiration = fetchedUserData.expiration || (Date.now() + 86400000);
              localStorage.setItem("expiration", JSON.stringify(expiration));

              /**Clear handoff cookie (match the domain it was set on)*/
              if (window.location.hostname === "localhost") {
                document.cookie = "handoff_email=; path=/; max-age=0";
              } else {
                document.cookie = "handoff_email=; domain=.commenter.ai; path=/; max-age=0";
              }

              /**
               * Signal consumers that userData is now hydrated. Providers like
               * AppStateProvider mount BEFORE this async handoff completes and
               * read an empty userData, so they skip their email-keyed fetches
               * (e.g. the Focus API) and never retry. Broadcasting this lets them
               * re-run once a valid email exists.
               */
              window.dispatchEvent(new Event("userData:hydrated"));
            }
          }

          if (currentUserData) {
            setUserDetail(currentUserData);
          }

          /**STEP 3 — Handle OAuth return*/
          if (params.get("account_id")) {
            setLoading(false);
            return;
          }

          /**STEP 4 — Normal connection check*/
          const response = await fetchAPI.get(
            `${config.urlPlatform}/api/v2/unipile/auth/getUserProfile`,
            { withCredentials: true }
          );

          if (response?.data?.success && response?.data?.data) {
            setLoading(false);
          } else {
            if (requireAuth) {
              navigate("/settings-new");
            } else {
              setLoading(false);
            }
          }
        } catch {
          if (requireAuth) {
            navigate("/settings-new");
          } else {
            setLoading(false);
          }
        }
      };

      bootstrap();
    }, [navigate]);

    if (loading) {
      return (
        <div className="min-h-full flex items-center justify-center bg-gray-950">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    const isAuthorized = isPaidPlanActive(userDetail);

    if (requireAuth && !isAuthorized) {
      return <Navigate to={urlPaths.unauthorized} replace />;
    }

    return children;
  };

  if (!featureToggle) {
    return null;
  }

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <GlobalStyle />
          <StickyCTA />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route exact path={urlPaths.login} element={<LoginPage />} />
              <Route
                exact
                path="/onboarding"
                element={
                  <>
                    <Seo title="Onboarding | Commenter AI" noindex />
                    <OnboardingPage />
                  </>
                }
              />
              <Route
                exact
                path={urlPaths.focusDataPolicy}
                element={<WhatFocusData />}
              />
              {/* <Route exact path={urlPaths.time} element={<Time />} /> */}
              {/* <Route
                exact
                path={urlPaths.successRegister}
                element={<SuccessRegisterPage />}
              /> */}
              <Route
                exact
                path={urlPaths.finalStep}
                element={<ExtensionLoginPage />}
              />
              <Route
                path="/health"
                element={<h3>Hey there! The App is Healthy!</h3>}
              />

              <Route element={<SalesPages />}>
                {/* <Route
                  exact
                  path={urlPaths.homepage}
                  element={<Homepage />}
                /> */}
                <Route exact path={urlPaths.aboutUs} element={<AboutUs />} />
                {
                  getHostNameConfig(
                    <Route
                      exact
                      path={urlPaths.pricing}
                      element={<Pricing />}
                    />
                  )
                }
                <Route exact path={urlPaths.results} element={<Results />} />
                <Route exact path={urlPaths.feature} element={<Feature />} />

                <Route
                  exact
                  path={urlPaths.privacyPolicy}
                  element={<PrivacyPolicyPage />}
                />
                <Route
                  exact
                  path={urlPaths.termsOfService}
                  element={<TermsPage />}
                />
                <Route
                  exact
                  path={urlPaths.contactUs}
                  element={<ContactPage />}
                />
                {/* <Route exact path={urlPaths.comingsoon} element={<ComingSoon />} /> */}
                {/* <Route
                  exact
                  path={urlPaths.comingsoon2}
                  element={<ComingSoon2 />}
                /> */}
                {/* <Route
                  exact
                  path={urlPaths.comingsoon}
                  element={<ComingSoon />}
                /> */}
                {/* <Route exact path={urlPaths.homenew} element={<HomeNew />} /> */}
                <Route exact path={urlPaths.homenew} element={<HomeV1 />} />
                <Route
                  exact
                  path={urlPaths.commenterhome}
                  element={<Commenterhome />}
                />
                {/* <Route exact path={urlPaths.thankYouPage} element={<ThankYouPage />} /> */}
                {/* <Route exact path={urlPaths.comingsoon2} element={<ComingSoon2 />} /> */}
              </Route >
              <Route element={<PublicPages />}>
                <Route
                  exact
                  path={urlPaths.register}
                  element={<RegisterPage />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                {/* <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                /> */}
              </Route >
              {/* <Route element={<ProPlan />}>
                <Route
                  exact
                  path={urlPaths.proplan + "/:email"}
                  element={<ProPlan />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                />
              </Route>
              <Route element={<Professional />}>
                <Route
                  exact
                  path={urlPaths.professional}
                  element={<Professional />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                />
              </Route>
              <Route element={<BlackFriday />}>
                <Route
                  exact
                  path={urlPaths.blackFriday}
                  element={<BlackFriday />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                />
              </Route >
              <Route element={<YearlyPlan />}>
                <Route
                  exact
                  path={urlPaths.yearlyPlan}
                  element={<YearlyPlan />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                />
              </Route >
              <Route element={<YearlyPlanEmail />}>
                <Route
                  exact
                  path={urlPaths.YearlyPlanEmail + "/:email"}
                  element={<YearlyPlanEmail />}
                />
                <Route
                  exact
                  path={urlPaths.requestNewPassword}
                  element={<RequestNewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.newPassword}
                  element={<NewPasswordPage />}
                />
                <Route
                  exact
                  path={urlPaths.unauthorized}
                  element={<Unauthorized />}
                />
                <Route
                  exact
                  path={urlPaths.sessionExpired}
                  element={<SessionExpired />}
                />
              </Route > */}
              <Route element={<MainPages />}>
                {
                  getHostNameConfig(
                    <Route
                      exact
                      path={urlPaths.platform}
                      element={<MainPage />}
                    />
                  )
                }
                <Route
                  exact
                  path={urlPaths.settings}
                  element={<ProtectedUnipileRoute requireAuth={false}><SettingsPage /></ProtectedUnipileRoute>}
                />
                {/* {
                  featureToggle.messaging &&
                  getHostNameConfig(
                    <Route
                      exact
                      path={urlPaths.messages}
                      element={
                        <ProtectedUnipileRoute>
                          <MessageCenterNew />
                        </ProtectedUnipileRoute>
                      }
                    />
                  )
                } */}
                {/* <Route exact path={urlPaths.messages} element={<MessageCenter 
            firstLoadChatsRef={firstLoadChatsRef}
            firstLoadMessagesRef={firstLoadMessagesRef}
            />} /> */}

                {/* {featureToggle.messaging && (
              <Route exact path={urlPaths.messages} element={<MessageCenter
                firstLoadChatsRef={firstLoadChatsRef}
                firstLoadMessagesRef={firstLoadMessagesRef}
              />} />
            )} */}

                {/* <Route
              exact
              path={urlPaths.getComments}
              element={<GetCommentsPage />}
            /> */}
                {/* <Route exact path={urlPaths.custom} element={<Custom />} /> */}
                {/* <Route exact path={urlPaths.custom} element={<CustomNew />} /> */}
                {/* {
                  getHostNameConfig(
                    <Route
                      exact
                      path={urlPaths.tutorial}
                      element={<Tutorial />}
                    />
                  )
                } */}

                {/* Tracking Dashboard Routes */}
                <Route exact path="/focus" element={<ProtectedUnipileRoute requireAuth={false}><FocusPage /></ProtectedUnipileRoute>} />
                <Route exact path="/inbox" element={<InboxWrapper />} />
                <Route
                  exact
                  path="/commenting"
                  element={<ProtectedUnipileRoute requireAuth={false}><Posts variant="commenting" /></ProtectedUnipileRoute>}
                />
                <Route exact path="/analytics" element={<AnalyticsPage />} />
                <Route
                  exact
                  path="/settings-new"
                  element={<SettingsPageNew />}
                />
                <Route
                  exact
                  path={urlPaths.preferences}
                  element={<PreferencesPage />}
                />

                {/* They are visible to only seven days trial and influencer only */}
                {/* Protected Unipile Routes starts */}
                {/* {
                  featureToggle.posts ? (
                    <Route
                      exact
                      path={urlPaths.posts}
                      element={
                        <ProtectedUnipileRoute>
                          <Posts />
                        </ProtectedUnipileRoute>
                      }
                    />
                  ) : null
                } */}
                <Route
                  exact
                  path={urlPaths.listposts}
                  element={<PostList />}
                />
                <Route
                  exact
                  path={urlPaths.peopleLists}
                  element={<PeopleList />}
                />
                <Route
                  exact
                  path={urlPaths.peopleSingle}
                  element={
                    // Lists are part of the trial too (free_trial allows every
                    // feature), so don't require a paid plan here, same as
                    // /commenting and /focus. The backend still blocks expired trials.
                    <ProtectedUnipileRoute requireAuth={false}>
                      <PeopleSingle />
                    </ProtectedUnipileRoute>
                  }
                />
                {/* <Route
                  path={urlPaths.dashboard}
                  element={
                    <ProtectedUnipileRoute>
                      <Dashboard />
                    </ProtectedUnipileRoute>
                  }
                /> */}
                {/* Protected Unipile Routes ends */}

                {/* <Route
                  exact
                  path={urlPaths.achievements}
                  element={<AchievementsPage />}
                /> */}
                {/* <Route
                  exact
                  path={urlPaths.requestFeature}
                  element={<RequestAFeaturePage />}
                /> */}
                {/* {
                  featureToggle.crm && (
                    <Route
                      exact
                      path={urlPaths.dashboardComingSoon}
                      element={
                        <ProtectedUnipileRoute>
                          <DashboardComingSoon />
                        </ProtectedUnipileRoute>
                      }
                    />
                  )
                } */}
                {/* <Route
                  exact
                  path={urlPaths.successSubscription}
                  element={<SuccessSubscriptionPage />}
                /> */}
                <Route
                  exact
                  path={urlPaths.checkoutRedirect}
                  element={<CheckoutRedirectPage />}
                />
                {/* <Route
                  exact
                  path={urlPaths.engagement}
                  element={<EngagementList />}
                /> */}
                {/* <Route
                  exact
                  path={urlPaths.marketplace}
                  element={<Marketplace />}
                /> */}
                {/* <Route
                  exact
                  path={urlPaths.savedComments}
                  element={<MyCommentList />}
                /> */}
                <Route
                  exact
                  path={urlPaths.postedComments}
                  element={<PostedComments />}
                />
                <Route
                  exact
                  path={urlPaths.qrLogin}
                  element={<QRLoginPage />}
                />
              </Route >
              {/* <Route
                exact
                path={urlPaths.surveyFinish}
                element={<SurveySuccess />}
              /> */}
              <Route path="*" element={<FallbackRedirect />} />
            </Routes >
          </Suspense>
          <Toaster />
        </TooltipProvider >
      </QueryClientProvider >
      {/* <FloatingWidget /> */}
    </div >
  );
}

export default App;
